import { ChatMessage } from '@portfolio/shared'
import { User, Bot, CheckCircle, AlertCircle, XCircle } from 'lucide-react'

interface ChatMessagesProps {
  messages: ChatMessage[]
}

export default function ChatMessages({ messages }: ChatMessagesProps) {
  const getConfidenceIcon = (confidence?: string) => {
    switch (confidence) {
      case 'high':
        return <CheckCircle size={16} className="text-green-500" />
      case 'medium':
        return <AlertCircle size={16} className="text-yellow-500" />
      case 'low':
        return <XCircle size={16} className="text-red-500" />
      default:
        return null
    }
  }

  const getConfidenceColor = (confidence?: string) => {
    switch (confidence) {
      case 'high':
        return 'border-green-400/60'
      case 'medium':
        return 'border-yellow-400/60'
      case 'low':
        return 'border-red-400/60'
      default:
        return 'border-white/20'
    }
  }

  return (
    <>
      {messages.map((message) => (
        <div
          key={message.id}
          className={`flex ${message.type === 'user' ? 'justify-end' : 'justify-start'} animate-slide-up`}
        >
          <div
            className={`max-w-xs sm:max-w-sm lg:max-w-md xl:max-w-lg px-3 py-2 sm:px-4 sm:py-3 rounded-lg backdrop-blur-sm ${
              message.type === 'user'
                ? 'bg-white/10 text-white border border-white/20'
                : `glass-message ${getConfidenceColor(message.confidence)}`
            }`}
          >
            <div className="flex items-start space-x-2">
              {message.type === 'assistant' && (
                <Bot size={16} className="text-blue-400 mt-0.5 flex-shrink-0 sm:w-5 sm:h-5" />
              )}
              {message.type === 'user' && (
                <User size={16} className="text-white mt-0.5 flex-shrink-0 sm:w-5 sm:h-5" />
              )}
              <div className="flex-1">
                <p className={`text-xs sm:text-sm ${message.type === 'user' ? 'text-white' : 'text-gray-200'}`}>
                  {message.content}
                </p>
                {message.type === 'assistant' && (
                  <div className="mt-2 flex items-center justify-between">
                    <div className="flex items-center space-x-1">
                      {getConfidenceIcon(message.confidence)}
                      <span className="text-xs text-gray-400">
                        {message.confidence === 'high' && '高信頼度'}
                        {message.confidence === 'medium' && '中信頼度'}
                        {message.confidence === 'low' && '低信頼度'}
                      </span>
                    </div>
                    <span className="text-xs text-gray-500">
                      {message.timestamp.toLocaleTimeString('ja-JP', {
                        hour: '2-digit',
                        minute: '2-digit'
                      })}
                    </span>
                  </div>
                )}
                {message.sources && message.sources.length > 0 && (
                  <div className="mt-2">
                    <p className="text-xs text-gray-500 mb-1">参考情報:</p>
                    <div className="flex flex-wrap gap-1">
                      {message.sources.map((source, index) => (
                        <span
                          key={index}
                          className="text-xs bg-white/10 text-blue-300 px-2 py-1 rounded border border-white/20"
                        >
                          {source}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      ))}
    </>
  )
}